import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from "@angular/core";
import { CasinoCardNewComponent } from "@components/casino/casino-card-new/casino-card-new.component";
import { GoogleDataInfo } from "@components/casino/google-data-info/google-data-info.component";
import { CONSTANTS } from "@constants";
import { IonicModule } from "@ionic/angular";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { Game } from "@models/lobby/casinoDataModels";
import { TranslateModule } from "@ngx-translate/core";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { Swiper, SwiperOptions } from "swiper";
import { CasinoLiveCardNewComponent } from "../casino-live-card-new/casino-live-card-new";

@Component({
  selector: "casino-category-slider-new",
  templateUrl: "./casino-category-slider-new.component.html",
  styleUrls: ["./casino-category-slider-new.component.scss"],
  imports: [
    CommonModule,
    IonicModule,
    CasinoCardNewComponent,
    CasinoLiveCardNewComponent,
    TranslateModule,
    GoogleDataInfo,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class CasinoCategorySliderNewComponent implements OnInit {
  @Input() id!: string;
  @Input() category!: any;
  @Input() controls = true;
  @Input() updatedBrJackpot: any = null;
  @Output() turnPlay = new EventEmitter<any>();

  @ViewChild("swiper") swiperRef:
    | ElementRef<HTMLElement & { swiper?: Swiper } & { initialize: () => void }>
    | undefined;

  backgroundImg = "";
  games: Game[] = [];
  type!: string;
  isGradientLeftAvailable = false;
  isGradientRightAvailable = false;
  isMobile = false;
  swiper?: Swiper;
  private viewIndex = 0;
  globalVars!: GlobalVarsModel;

  cd = inject(ChangeDetectorRef);
  newBaseService = inject(NewBaseService);
  devicesService = inject(DeviceService);

  readonly lobbyConstants = CONSTANTS;

  constructor() {
    this.newBaseService.getVars.subscribe((data: GlobalVarsModel) => (this.globalVars = data));
    this.isMobile = this.devicesService.isMobile();
    if (this.isMobile) this.isGradientRightAvailable = true;
  }

  ngOnInit(): void {
    if (!this.category) return;
    this.games = this.category.Games ?? [];
    this.type = this.category.typeCategory ?? "";
    if (this.type === "Bonus") this.isGradientRightAvailable = false;
    if (this.category.BackgroundImageUrl) this.backgroundImg = `url(${this.category.BackgroundImageUrl})`;
    if (this.type === this.lobbyConstants.bigSquareVertical) {
      this.setGameTileSize();
    }
  }

  ngAfterViewInit(): void {
    if (!this.swiperRef?.nativeElement.swiper) {
      const config: SwiperOptions = {
        slidesPerView: "auto",
        freeMode: true,
        allowTouchMove: !this.controls,
        spaceBetween: this.controls ? 18 : 0,
        navigation: {
          nextEl: `.swiper-button-next-${this.id}`,
          prevEl: `.swiper-button-prev-${this.id}`,
        },
        observer: true,
      };
      const swiperEl = Object.assign(this.swiperRef?.nativeElement, config);
      swiperEl.initialize();
      this.swiper = this.swiperRef?.nativeElement.swiper;
      this.swiper.on("slideChange", () => {
        this.viewIndex = this.swiper.activeIndex;
        this.cd.detectChanges();
      });
    }
  }

  get vi(): number {
    return this.viewIndex + 8;
  }

  trackByGame(index: number, game: Game): string {
    return game?.GameId ?? index.toString();
  }

  changeSlide(option: "prev" | "next") {
    if (!this.swiper) return;
    option === "prev" && !this.swiper.isBeginning
      ? this.swiper.slidePrev()
      : option === "next" && !this.swiper.isEnd
        ? this.swiper.slideNext()
        : null;
  }

  getCustomClass(game: Game): string {
    let cssClass: string;
    switch (this.type) {
      case this.lobbyConstants.vertical:
        cssClass = "vertical-slide";
        break;
      case this.lobbyConstants.verticalBig:
        cssClass = "bigSquareVertical-slide";
        break;
      case this.lobbyConstants.bigSquare:
        cssClass = "bigSquare-slide";
        break;
      case this.lobbyConstants.bigSquareVertical:
        cssClass = game.gameTileSize === this.lobbyConstants.vertical ? "vertical-slide" : "bigSquare-slide";
        break;
      case this.lobbyConstants.rounded:
        cssClass = "swiper-slide-custom rounded-slide";
        break;
      case "Bonus":
        cssClass = game.gameTileSize === "Bigrectangle" ? "bonus bigrectangle" : "bonus swiper-slide-custom";
        break;
      default:
        cssClass = game.gameTileSize === "Bigrectangle" ? "bigrectangle" : "swiper-slide-custom";
    }
    return cssClass;
  }

  getCardClass(game: Game): string {
    let cssClass: string;
    switch (this.type) {
      case this.lobbyConstants.vertical:
        cssClass = "vertical";
        break;
      case this.lobbyConstants.verticalBig:
        cssClass = "vertical";
        break;
      case this.lobbyConstants.bigSquare:
        cssClass = "bigSquare";
        break;
      case this.lobbyConstants.bigSquareVertical:
        cssClass = game.gameTileSize === this.lobbyConstants.vertical ? "vertical" : "bigSquare";
        break;
      case this.lobbyConstants.rounded:
        cssClass = "rounded";
        break;
      case "Bonus":
        cssClass = "bonus-card";
        break;
      default:
        cssClass = "";
    }
    return cssClass;
  }

  isVertical(game: Game): boolean {
    return (
      [this.lobbyConstants.vertical, this.lobbyConstants.verticalBig].includes(this.type) ||
      game.gameTileSize === this.lobbyConstants.vertical
    );
  }

  setGameTileSize() {
    this.games = this.games.map((game, i) => {
      if (i % 2 === 0) {
        game.gameTileSize = this.lobbyConstants.vertical;
      }
      return game;
    });
  }

  get isRounded(): boolean {
    return this.type === this.lobbyConstants.rounded;
  }

  btPlayDetail(event: any) {
    this.turnPlay.emit(event);
  }
}
